<template>
  <v-main>
    <v-container fluid style="height: 100%" class="flex flex-col">
      <div class="flex items-center pl-3">
        <div class="text-xl" data-testid="admin-accounts-title">
          {{ t("accounts.title") }}
        </div>
        <v-spacer></v-spacer>
        <v-btn-toggle
          v-model="tab"
          mandatory
          style="height: 40px"
          density="compact"
          class="mr-3"
          variant="outlined"
          color="primary"
          divided
        >
          <v-btn value="actived"> {{ t("accounts.active") }} </v-btn>

          <v-btn value="archived"> {{ t("accounts.archived") }} </v-btn>
        </v-btn-toggle>
        <v-text-field
          ref="searchInputRef"
          v-model="searchQuery"
          color="primary"
          :placeholder="t('common.search')"
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="compact"
          class="mr-3"
          variant="outlined"
          style="max-width: 300px"
        >
          <template #append-inner>
            <span :class="['border-theme rounded text-disabled text-xs px-1']">
              <span>{{ shortcutLabel }}</span>
            </span>
          </template>
        </v-text-field>
      </div>

      <v-data-table-virtual
        v-model:sort-by="sortBy"
        :headers="headers"
        :items="processedAccounts"
        :loading="loadingAccounts"
        :header-props="{ class: 'text-left font-bold' }"
        item-value="id"
        hover
        fixed-header
        class="mt-6 grow"
        style="min-height: 0"
      >
        <template #loading>
          <v-skeleton-loader type="table-tbody@2"></v-skeleton-loader>
        </template>

        <template #item="{ item, props: rowProps, itemRef }">
          <RowLink
            v-bind="rowProps"
            :ref="itemRef"
            :to="{
              name: 'Account',
              params: { accountId: item.id },
              query: { supervisorOrgId: item.supervisorOrgId },
            }"
          >
            <RowLinkCell width="100px">
              <AccountStatusIcon :account="item" style="z-index: 1" />
            </RowLinkCell>
            <RowLinkCell focusable>
              <v-badge
                v-if="item.pendingTransfersCount && item.pendingTransfersCount > 0"
                v-tooltip="t('accounts.pendingTransfers', { count: item.pendingTransfersCount })"
                style="z-index: 1; position: relative"
                color="warning"
                :content="item.pendingTransfersCount"
                inline
              ></v-badge>
              {{ item.name }}
            </RowLinkCell>
            <RowLinkCell width="160px">{{ item.supervisorOrgName }}</RowLinkCell>
            <RowLinkCell width="160px">
              <AircapLogo
                v-if="getProviderType(item.accountType) === 'proAccount'"
                v-tooltip="t('accounts.accountPro')"
                style="z-index: 1; position: relative"
                size="16px"
              />
              <v-icon
                v-else
                v-tooltip="t('accounts.openBanking') + ' (' + item.accountType + ')'"
                style="z-index: 1"
                size="small"
                >mdi-bank-outline</v-icon
              >
            </RowLinkCell>
            <RowLinkCell width="200px">{{ item.balanceFormatted }}</RowLinkCell>
          </RowLink>
        </template>

        <template #no-data>
          <div class="text-center py-3">{{ t("accounts.noAccounts") }}</div>
        </template>
      </v-data-table-virtual>
    </v-container>
  </v-main>
</template>

<script lang="ts" setup>
import AccountStatusIcon from "@/components/AccountStatusIcon.vue";
import AircapLogo from "@/components/AircapLogo.vue";
import RowLink from "@/components/RowLink/RowLink.vue";
import RowLinkCell from "@/components/RowLink/RowLinkCell.vue";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { formatCurrency } from "@/utils/tools";
import type { Account } from "@aircap/common/account";
import {
  getAccountBalance,
  getAccountStatusSortPriority,
  getProviderType,
} from "@aircap/common/account";
import type { SupervisorOrg } from "@aircap/common/supervisorOrg";
import { supervisorOrgsFullPath } from "@aircap/common/supervisorOrg";
import { collection, query, where } from "firebase/firestore";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useDisplay } from "vuetify";
import type { VTextField } from "vuetify/components";

type WithId<T> = T & { id: string };
type AccountDoc = WithId<Account>;

type DisplayAccount = AccountDoc & {
  supervisorOrgName: string | null;
  balanceFormatted: string;
  balanceNumeric: number;
  currency: string;
  pendingTransfersCount?: number;
};

type SortItem = { key: string; order: "asc" | "desc" };

const { t, locale } = useI18n();
const tab = ref<"actived" | "archived">("actived");
const searchQuery = ref("");
const searchInputRef = ref<InstanceType<typeof VTextField> | null>(null);
const sortBy = ref<SortItem[]>([{ key: "status", order: "asc" }]);
const { platform } = useDisplay();

const shortcutLabel = computed(() => {
  return platform.value.mac ? "Cmd+K" : "Ctrl+K";
});

const headers = computed(() => [
  {
    title: t("common.status"),
    key: "status",
    sortable: true,
    width: "100px",
    sortRaw: (a: DisplayAccount, b: DisplayAccount) =>
      getAccountStatusSortPriority(a) - getAccountStatusSortPriority(b) ||
      a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()) ||
      (a.supervisorOrgName && b.supervisorOrgName
        ? a.supervisorOrgName
            .toLocaleLowerCase()
            .localeCompare(b.supervisorOrgName.toLocaleLowerCase())
        : 0) ||
      (a.createdAt && b.createdAt ? b.createdAt.seconds - a.createdAt.seconds : 0),
  },
  { title: t("common.name"), key: "name", sortable: true },
  {
    title: t("common.supervisorOrg"),
    key: "supervisorOrgName",
    sortable: true,
    width: "160px",
    sortRaw: (a: DisplayAccount, b: DisplayAccount) =>
      (a.supervisorOrgName ?? "")
        .toLocaleLowerCase()
        .localeCompare((b.supervisorOrgName ?? "").toLocaleLowerCase()),
  },
  { title: t("common.type"), key: "accountType", sortable: true, width: "160px" },
  {
    title: t("common.balance"),
    key: "balanceFormatted",
    sortable: true,
    width: "200px",
    sortRaw: (a: DisplayAccount, b: DisplayAccount) => a.balanceNumeric - b.balanceNumeric,
  },
]);

const accountsSource = computed(() => {
  return query(
    collection<Account>(db, "accounts"),
    where<Account>("isArchived", "==", tab.value === "archived"),
  );
});

const supervisorOrgsSource = computed(() => {
  return query(collection<SupervisorOrg>(db, supervisorOrgsFullPath));
});

const { data: accountsData, pending: loadingAccounts } = useCollection<Account>(accountsSource);
const { data: supervisorOrgsData } = useCollection<SupervisorOrg>(supervisorOrgsSource);

const supervisorOrgsById = computed(() => {
  const map = new Map<string, string>();
  for (const supervisorOrg of supervisorOrgsData.value ?? []) {
    map.set(supervisorOrg.id, supervisorOrg.name);
  }
  return map;
});

const processedAccounts = computed<DisplayAccount[]>(() => {
  const all = accountsData.value ?? [];

  const search = searchQuery.value.trim().toLocaleLowerCase();
  const filtered = search
    ? all.filter((p) => {
        return (
          p.name.toLocaleLowerCase().includes(search) ||
          p.id.toLocaleLowerCase().includes(search) ||
          (p.supervisorOrgId
            ? (supervisorOrgsById.value.get(p.supervisorOrgId)?.toLocaleLowerCase() ?? "").includes(
                search,
              )
            : false)
        );
      })
    : all;

  return filtered.map((account) => {
    const { balance, currency = "EUR" } = getAccountBalance(account);

    return {
      ...account,
      supervisorOrgName: account.supervisorOrgId
        ? (supervisorOrgsById.value.get(account.supervisorOrgId) ?? null)
        : null,
      id: account.id,
      balanceFormatted:
        balance !== undefined
          ? formatCurrency({ value: balance, currencyCode: currency, locale: locale.value })
          : "",
      balanceNumeric: balance ?? 0,
      currency,
      pendingTransfersCount: account.pendingTransfersCount ?? 0,
    } satisfies DisplayAccount;
  });
});

const handleKeyDown = (event: KeyboardEvent) => {
  if ((event.ctrlKey || event.metaKey) && event.key === "k") {
    event.preventDefault();
    searchInputRef.value?.focus();
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
});
</script>

<style scoped>
:deep(.v-field__outline) {
  --v-field-border-opacity: 0.12;
}
</style>
